import type { RoundKind } from './rounds';

/**
 * Broker: Polarium Broker, through the Quadcode client SDK (packages/polarium).
 *
 * Each round clock runs on its own asset (owner's decision 2026-10-22), set once per server
 * process at startup: Sobe e Desce on EUR/USD OTC (POLARIUM_RISE_FALL_SYMBOL), Dígitos on
 * EUR/GBP OTC (POLARIUM_DIGITS_SYMBOL).
 * No Polarium Blitz asset trades 24/7 (official schedules read 2026-10-22: every one has 30-min
 * pauses): EUR/GBP Mon/Wed/Fri 01:00–01:30 Brasília, EUR/USD daily 05:00–05:30.
 * A Blitz option's entry quote is the price at the purchase instant, so a
 * round's entries are bought IN the round's open second.
 */
export const DEFAULT_ROUND_SYMBOLS: Readonly<Record<RoundKind, string>> = { RISE_FALL: 'EURUSD-OTC', EVEN_ODD: 'EURGBP-OTC' };

let rounds: Record<RoundKind, string> = { ...DEFAULT_ROUND_SYMBOLS };

export function configureRounds(symbols: Partial<Record<RoundKind, string>>): void {
  for (const [kind, symbol] of Object.entries(symbols) as [RoundKind, string | undefined][]) if (symbol) rounds[kind] = symbol;
}

/** The asset a round clock runs on. */
export function roundSymbolFor(kind: RoundKind): string {
  return rounds[kind];
}

/** Every clock's asset: { RISE_FALL, EVEN_ODD }. */
export function roundSymbolMap(): Readonly<Record<RoundKind, string>> {
  return rounds;
}

/** Assets the timed rounds run on (each once). */
export function roundSymbols(): readonly string[] {
  return [...new Set(Object.values(rounds))];
}

/** Clocks that run on this asset (none for a Traderoom-only asset). */
export function roundKindsOf(symbol: string): RoundKind[] {
  return (Object.keys(rounds) as RoundKind[]).filter((k) => rounds[k] === symbol);
}

/** Sobe e Desce's asset: robots (Sobe/Desce only on Polarium) and the dormant Rush. */
export function primaryRoundSymbol(): string {
  return rounds.RISE_FALL;
}

/**
 * Quadcode tickers as symbols: EURUSD, EURUSD-OTC, XAUUSD-OTC, AAPL, and composite ones such as
 * S-P-500 or TESLA-FORD-OTC (the bridge turns every other character into "-").
 */
export const POLARIUM_SYMBOL_RE = /^(?=.{2,30}$)[A-Z0-9]+(?:-[A-Z0-9]+)*$/;

/** Display name of a Quadcode ticker: EURUSD-OTC → EUR/USD (OTC). */
export function polariumSymbolName(symbol: string): string {
  const m = /^([A-Z]{3})([A-Z]{3})(-OTC)?$/.exec(symbol);
  if (m) return `${m[1]}/${m[2]}${m[3] ? ' (OTC)' : ''}`;
  return symbol.replace(/-OTC$/, ' (OTC)');
}
